import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import IconInstagram from "@/components/icons/icon-instagram";
import IconFacebook from "@/components/icons/icon-facebook";
import IconArrowRightUp from "@/components/icons/icon-arrow-right-up";
import IconArrowRight from "@/components/icons/icon-arrow-right";
import IconWhatsapp from "@/components/icons/icon-whatsapp";
import { cn } from "@/lib/utils";
import IconSnapchat from "@/components/icons/icon-x";

const services = [
  {
    id: 1,
    title: "Effortless Table Booking",
    description:
      "Reserve your table in under a minute—no calls, no hassle. Enjoy your meal exactly when you want it.",
    cta: "Book Now",
    href: "https://wa.me/8801830000233",
  },
  {
    id: 2,
    title: "Traditional Arabian Dishes",
    description:
      "Over 100 Arabian dishes crafted by expert chefs, bringing tradition and flavor straight to your plate.",
    cta: "Explore menu",
    href: "/menu",
  },
  {
    id: 3,
    title: "Elegant & Inviting Space",
    description:
      "Experience interiors inspired by Arabian geometry and modern design. Perfect for dinners, dates, or gatherings.",
    cta: "Take a Look",
    href: "/gallery",
  },
];

const socials = [
  {
    href: "https://www.instagram.com/almajlis_arabian",
    icon: <IconInstagram />,
  },
  {
    href: "https://www.facebook.com/almajlisarabianrestaurant",
    icon: <IconFacebook />,
  },
  {
    href: "https://snapchat.com/t/DO1BznTR",
    icon: <IconSnapchat />,
  },
];

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="w-full lg:min-h-svh pb-10 pt-[253.3px] bg-[url('/imgs/hero-bg.png')] bg-cover bg-center bg-no-repeat relative overflow-hidden"
    >
      {/* Animated overlay for better readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60 z-0"></div>

      <div className="max-w-[1120.13px] w-full mx-auto text-center px-5 relative z-10">
        {/* Main heading with fade-in and slide-up animation */}
        <h1
          className={cn(
            "text-[33px] sm:text-6xl lg:text-[96px] leading-[86%] font-bold font-amiri text-spring-wood mb-5 lg:mb-7 transform transition-all duration-1000 ease-out",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          )}
        >
          Authentic Arabian Dining in the Heart of Dhaka
        </h1>

        <div className="space-y-6 lg:space-y-[55px]">
          {/* Subtitle with staggered animation */}
          <p
            className={cn(
              "max-w-[370px] lg:max-w-[773.48px] w-full mx-auto text-sm lg:text-[28px]/[127%] font-medium text-old-lace transform transition-all duration-1000 delay-300 ease-out",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            )}
          >
            Where tradition meets taste — handcrafted dishes from the sands to
            your table
          </p>

          {/* CTA buttons with staggered animation */}
          <div
            className={cn(
              "center flex-col md:flex-row gap-2.5 lg:gap-6.5 transform transition-all duration-1000 delay-500 ease-out",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            )}
          >
            <Link href="/menu">
              <button className="max-md:max-w-[368px] max-md:w-full center h-[52px] md:h-[67px] px-[35px] border border-white bg-dolly text-mirage text-[22px] rounded-[25px] flex items-center gap-2 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:bg-dolly/90">
                View Menu <IconArrowRightUp />
              </button>
            </Link>
            <Link href={"https://wa.me/8801830000233"}>
              <button className="max-md:max-w-[368px] max-md:w-full center h-[52px] md:h-[67px] px-[35px] border border-white bg-white/[1%] backdrop-blur-[23.55px] text-white text-[22px] rounded-[25px] flex items-center gap-2 overflow-hidden transition-all duration-300 hover:scale-105 hover:bg-white/10 hover:shadow-lg">
                Reserve A Table <IconArrowRightUp />
              </button>
            </Link>
          </div>
        </div>

        {/* Service cards with staggered animation */}
        <div
          className={cn(
            "flex items-center justify-center gap-[5px] lg:gap-10 mt-[146px] lg:mt-[176px] px-0 transform transition-all duration-1000 delay-700 ease-out",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          )}
        >
          {services.map((service, index) => (
            <div
              key={service.id}
              className={cn(
                "max-w-[183.07px] lg:max-w-[305px] w-full rounded-[30px] border border-white/[57%] bg-black/5 backdrop-blur-[15px] p-2.5 lg:py-3.75 lg:px-4.5 flex flex-col gap-[3px] lg:gap-1.25 transition-all duration-500 hover:scale-105 hover:bg-black/20 hover:shadow-lg",
                index === 2 && "max-lg:hidden",
                `delay-${index * 200}`
              )}
            >
              <h2 className="text-[17.03px] lg:text-[25px] leading-[18.3px] lg:leading-[26.8px] font-amiri text-white">
                {service.title}
              </h2>
              <p className="text-[9.54px] lg:text-sm lg:leading-[20px] text-old-lace">
                {service.description}
              </p>
              <Link
                href={service.href}
                className="w-full flex items-center justify-center gap-2 text-[9.14px] lg:text-[13.42px] font-semibold font-inter text-old-gold mt-2 transition-all duration-300 hover:gap-3"
              >
                {service.cta} <IconArrowRight className="max-lg:w-[9.14px]" />
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Socials with fade-in animation */}
      <div
        className={cn(
          "hidden absolute bottom-10 left-2 2xl:left-20 xl:flex flex-col gap-7.5 items-center transform transition-all duration-1000 delay-1000 ease-out",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        )}
      >
        <p className="rotate-90 text-sm text-white mb-7.5">FOLLOW US:</p>
        <div className="w-[1px] h-[80px] bg-white"></div>
        <div>
          <ul className="flex flex-col items-center gap-7.5">
            {socials.map((social, index) => (
              <li
                key={index}
                className="transition-all duration-300 hover:scale-125"
              >
                <Link href={social.href}>{social.icon}</Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div
        className={cn(
          "block absolute bottom-50 left-2 z-10 2xl:left-20 xl:hidden flex-col gap-7.5 items-center transform transition-all duration-1000 delay-1000 ease-out",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        )}
      >
        <p className="rotate-90 text-sm text-white mb-0">FOLLOW US:</p>
        <div className="w-[1px] h-[80px] bg-white"></div>
        <div>
          <ul className="flex flex-col items-center gap-7.5">
            {socials.map((social, index) => (
              <li
                key={index}
                className="transition-all duration-300 hover:scale-125"
              >
                <Link href={social.href}>{social.icon}</Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* WhatsApp call with continuous rotation animation */}
      <button
        className={cn(
          "hidden xl:block absolute bottom-10 right-2 2xl:right-20 transform transition-all duration-1000 delay-1000 ease-out",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        )}
      >
        <div className="w-[132px] h-[132px] rounded-full overflow-hidden relative center isolate">
          <Image
            src={"/imgs/cta-circle.png"}
            alt="al-majlish-cta"
            width={132}
            height={132}
            className="w-full h-full object-contain absolute top-0 left-0 animate-spin z-[-2]"
            style={{ animationDuration: "10s" }}
            priority
          />
          <a
            href="tel:+8801830000233"
            className="size-[60px] bg-teak rounded-full center transition-all duration-300 hover:scale-110"
          >
            <IconWhatsapp />
          </a>
        </div>
      </button>

      {/* Floating elements for visual interest */}
      <div className="absolute top-1/4 left-10 w-6 h-6 rounded-full bg-old-gold/30 animate-pulse delay-1000"></div>
      <div className="absolute top-1/3 right-20 w-8 h-8 rounded-full bg-dolly/40 animate-pulse delay-500"></div>
      <div className="absolute bottom-1/4 left-20 w-10 h-10 rounded-full bg-teak/30 animate-pulse delay-1500"></div>
    </section>
  );
};

export default HeroSection;
